import { useMemo } from 'react'

import type { Filter, TimeDimension } from '@cubejs-client/core'
import { resultSetToTableData } from 'cube'
import { DRIFT_CHAT_OPPORTUNITY_OVERVIEW } from 'cube/constants'
import moment from 'moment'

import {
  type CubeQuery,
  CircularChartSeriesData,
  CubeQueryBuilder,
  useCubeQuery,
} from '@driftt/dds.reports'

import {
  OpportunitiesBaseQuery,
  OpportunitiesTableRow,
  OpportunityCountBaseQuery,
} from 'layouts/RevenueReport'
import { getInfluencedAndSourcedFilters } from 'layouts/RevenueReport/RevenueKPICards/RevenueKPICards.helpers'

import { useOverviewStore } from './Overview.context'
import { OpportunityTypesTableRow } from './Overview.types'

const dateFormat = 'YYYY-MM-DD'

export const useOpportunities = ({
  isInfluenced = false,
  isSourced = false,
}) => {
  const {
    store: { startDate, endDate },
  } = useOverviewStore()

  const timeDimension: TimeDimension = {
    dimension: DRIFT_CHAT_OPPORTUNITY_OVERVIEW.OPPORTUNITY_CREATED_AT,
    dateRange: [
      moment(startDate).format(dateFormat),
      moment(endDate).format(dateFormat),
    ],
  }

  const filtersArray: Filter[] = getInfluencedAndSourcedFilters({
    isInfluenced,
    isSourced,
  })

  const topOpportunitiesDataCubeQuery = new CubeQueryBuilder({
    query: OpportunitiesBaseQuery,
  })
    .withOrder([[DRIFT_CHAT_OPPORTUNITY_OVERVIEW.OPPORTUNITY_AMOUNT, 'desc']])
    .withFilters(filtersArray)
    .withLimit(5)
    .withTimeDimensions([timeDimension])
    .build()

  const opportunityTypesCubeQuery: CubeQuery = new CubeQueryBuilder({
    query: OpportunityCountBaseQuery,
  })
    .withName(`OpportunityCount-Overview`)
    .withQueryName(`OpportunityCount-Overview`)
    .withDimensions([DRIFT_CHAT_OPPORTUNITY_OVERVIEW.OPPORTUNITY_TYPE])
    .withFilters(filtersArray)
    .withTimeDimensions([timeDimension])
    .build()

  const {
    data: topOpportunitiesResultData,
    isLoading: isLoadingTopOpportunitiesData,
    isError: isErrorTopOpportunitiesData,
  } = useCubeQuery({
    query: topOpportunitiesDataCubeQuery,
  })

  const {
    data: opportunityTypesResultSet,
    isLoading: isLoadingTypesCubeData,
    isError: isErrorFetchingTypesCubeData,
  } = useCubeQuery({
    query: opportunityTypesCubeQuery,
  })

  const topOpportunitiesData = useMemo(() => {
    const opportunityTypesData: OpportunitiesTableRow[] = resultSetToTableData(
      topOpportunitiesResultData
    ) as OpportunitiesTableRow[]
    return opportunityTypesData || []
  }, [topOpportunitiesResultData])

  const [opportunityTypesSeriesData, totalCountOfFilteredOpportunitites] =
    useMemo(() => {
      const opportunityTypesData: OpportunityTypesTableRow[] =
        (resultSetToTableData(
          opportunityTypesResultSet
        ) as OpportunityTypesTableRow[]) || []

      const mappedDataToSeriesData: CircularChartSeriesData =
        opportunityTypesData.map((row: OpportunityTypesTableRow) => {
          return {
            name: row.opportunityType,
            y: parseInt(row.count),
          }
        })

      const totalReducedCount = opportunityTypesData.reduce((acc, row) => {
        return acc + parseInt(row.count)
      }, 0)

      return [mappedDataToSeriesData, totalReducedCount]
    }, [opportunityTypesResultSet])

  return useMemo(
    () => ({
      isLoadingTopOpportunitiesData,
      isErrorTopOpportunitiesData,
      topOpportunitiesData,
      opportunityTypesSeriesData,
      totalCountOfFilteredOpportunitites,
      isLoadingTypesCubeData,
      isErrorFetchingTypesCubeData,
      topOpportunitiesDataCubeQuery,
    }),
    [
      isErrorTopOpportunitiesData,
      isErrorFetchingTypesCubeData,
      isLoadingTopOpportunitiesData,
      isLoadingTypesCubeData,
      opportunityTypesSeriesData,
      totalCountOfFilteredOpportunitites,
      topOpportunitiesData,
      topOpportunitiesDataCubeQuery,
    ]
  )
}

export default useOpportunities
